import { Button, Modal, Pagination, Table, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { studentIdSet } from "../../Student/StudentSlice";
import {
	NOTIFICATION_TYPES,
	openNotification,
} from "../../Notifications/NotificationsUtils";
import { StudentCVAccessDTO } from "../../../Api";
import { useQuery } from "react-query";
import useQueryFilters from "../../../Hooks/useQueryFilters";
import { downloadExcel, downloadFile } from "../../../utils/downloadUtils";
import { useColumns } from "./columns";
import { removeDiacritics } from "../../../utils/dataUtils";
import {
	generateStudentAccessQRCodePdf,
	generateStudentAccessQRCodePng,
	getAllStudentCVsForCompany,
	getStudentCVAccessFilters,
} from "../../../Requests/polijobs-market-requests";
import Filters from "../../Filters";
import { useGetCompanyId, useIsCompany } from "../../../utils/utilFunctions";

export const StudentCVsView = (props: { companyId: string }) => {
	const { t, i18n } = useTranslation();
	const dispatch = useDispatch();
	const history = useHistory();
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [downloadError, setDownloadError] = useState(false);
	const isCompany = useIsCompany();
	const userCompanyId = useGetCompanyId();

	const [isQrModalVisible, setIsQrModalVisible] = useState(false);
	const [qrImage, setQrImage] = useState<string | null>(null);

	const query = useQueryFilters({});

	const goToStudentProfile = (id: any) => {
		dispatch(studentIdSet(id));
		history.push("/student");
	};

	const columns = useColumns(goToStudentProfile) as any[];
	const excelColumns = columns.filter((column) => column.key !== "cvFileName");

	const openGetErrorNotification = (_error: any) => {
		openNotification(
			t("usersText.errorTexts.failedUserGet"),
			t("usersText.errorTexts.serverFailedDescription"),
			NOTIFICATION_TYPES.ERROR
		);
	};

	const openFilterErrorNotification = (_error: any) => {
		openNotification(
			t("internships.error"),
			t("internships.filterDataError"),
			NOTIFICATION_TYPES.ERROR
		);
	};

	const { data: filters } = useQuery(
		["getStudentCVAccessFilters", isCompany, props.companyId],
		() => {
			const companyIdToUse = isCompany ? userCompanyId : props.companyId;
			if (companyIdToUse) {
				return getStudentCVAccessFilters(companyIdToUse, i18n.language);
			} else {
				return [];
			}
		},
		{
			onError: openFilterErrorNotification,
			refetchOnWindowFocus: false,
		}
	);

	const companyIdToUse = isCompany ? userCompanyId : props.companyId;

	const { data, isLoading: loading } = useQuery(
		["getStudentCVAccess", companyIdToUse, query.filters],
		() => {
			if (!companyIdToUse) return null;
			const parsedFilters = {
				...query.filters,
				page: query.filters?.page,
				pageSize: query.filters?.pageSize,
			};
			return getAllStudentCVsForCompany(companyIdToUse, parsedFilters as any);
		},
		{
			onError: openGetErrorNotification,
		}
	);

	const onUpdate = useCallback(
		(values: { [key: string]: string[] }) => {
			query.update(values, true);
		},
		[query]
	);

	const onPageUpdate = useCallback(
		(page: number, pageSize: number) => {
			setCurrentPage(page);
			setPageSize(pageSize);
			query.update({
				page: page.toString(),
				pageSize: pageSize.toString(),
			});
		},
		[query]
	);

	const downloadStudentsList = useCallback(async () => {
		try {
			const totalPages = Math.ceil((data?.totalCount || 0) / pageSize);
			const allData: StudentCVAccessDTO[] = [];

			const companyId = companyIdToUse;
			for (let page = 1; page <= totalPages; page++) {
				const response = await getAllStudentCVsForCompany(companyId!, {
					page: page,
					pageSize: pageSize,
					...query.filters,
				});
				allData.push(...(response.data || []));
			}

			await downloadExcel(
				t("files.studentCVAccess"),
				excelColumns.map((e) => {
					return {
						header:
							typeof e.title === "string"
								? removeDiacritics(e.title)
								: removeDiacritics(t("applicants." + e.key)),
						key: e.key.toString(),
					};
				}),
				allData
			);

			setDownloadError(false);
		} catch (error) {
			setDownloadError(true);
			openNotification(
				t("reports.errorTexts.downloadFailed"),
				t("reports.errorTexts.downloadFailedMessage"),
				NOTIFICATION_TYPES.ERROR
			);
		}
	}, [t, columns, query.filters, data?.totalCount, pageSize]);

	const downloadFail = (_err: any) => {
		openNotification(
			t("polijobs.error"),
			t("polijobs.downloadError"),
			NOTIFICATION_TYPES.ERROR
		);
	};

	const showQrModal = async () => {
		try {
			const res = await generateStudentAccessQRCodePng();
			const blob = new Blob([res], { type: "image/png" });
			const url = URL.createObjectURL(blob);
			setQrImage(url);
			setIsQrModalVisible(true);
		} catch (err) {
			downloadFail(err);
		}
	};

	return (
		<div className="px-4 py-6 flex flex-col">
			<Filters
				filters={filters}
				searchFields={[{ name: "search", label: t("internships.search") }]}
				onUpdate={onUpdate}
				hasSort={false}
			/>

			<div className="flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-3 mb-4">
				{isCompany && (
					<>
						<Button
							type="primary"
							title={t("polijobs.viewQRCode")}
							onClick={showQrModal}
							className="w-full sm:w-auto"
						>
							{t("polijobs.viewQRCode")}
						</Button>
						<Button
							type="primary"
							title={t("polijobs.downloadQRCode")}
							onClick={() =>
								downloadFile(
									generateStudentAccessQRCodePdf(userCompanyId),
									t("documents.QR")
								).catch(downloadFail)
							}
							className="w-full sm:w-auto"
						>
							{t("polijobs.downloadQRCode")}
						</Button>
					</>
				)}
				<Button onClick={downloadStudentsList} className="w-full sm:w-auto">
					{t("polijobs.downloadStudentCVAccessList")}
				</Button>
			</div>

			<div className="flex flex-col gap-6">
				<div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
					<Typography.Title
						level={4}
						className="my-0 text-base sm:text-lg md:text-xl"
					>
						{data?.totalCount} {t("filtering.results")}
					</Typography.Title>
					{!loading && (
						<Pagination
							size="small"
							className="self-start md:self-auto"
							current={Number(query.filters.page) || data?.page || 1}
							pageSize={Number(query.filters.pageSize) || data?.pageSize}
							defaultCurrent={data?.page}
							defaultPageSize={data?.pageSize}
							total={data?.totalCount}
							onChange={onPageUpdate}
							locale={{ items_per_page: t("pagination") }}
						/>
					)}
				</div>

				<div className="overflow-x-auto">
					<Table
						locale={{ emptyText: t("tableText.emptyTable") }}
						columns={columns}
						dataSource={data?.data ?? []}
						pagination={false}
						scroll={{ x: 1000, y: 420 }}
						rowKey={(record) => record.studentId!}
					/>
				</div>
			</div>

			<Modal
				title={t("polijobs.viewQRCode")}
				open={isQrModalVisible}
				onCancel={() => setIsQrModalVisible(false)}
				footer={null}
				centered
			>
				{qrImage && (
					<img
						src={qrImage}
						alt="QR Code"
						className="w-full max-w-[250px] sm:max-w-[300px] mx-auto block"
					/>
				)}
			</Modal>
		</div>
	);
};
